import Details from 'components/product/detail';
import Main from '../../../layouts/main';
import { useSelector } from 'react-redux';
import Dashboard from '../../../layouts/dashboard';
import request from '../../../tools/request';
import Head from 'next/head';
import React, { useMemo } from 'react';
import useTranslation from '../../../hooks/useTranslation';
import { useRouterWithLang } from 'hooks/useRouterWithLang';

function Page({ product }) {
  const { locale, TRANSLATIONS, translationsParameters } = useTranslation();
  const isLoggedIn = useSelector((s) => s.isLoggedIn);
  const router = useRouterWithLang();

  const amountSaving = useMemo(() => {
    let amount = (product?.streetPrice - product?.bestTaxable).toFixed(2);
    if (amount <= 10) {
      return (product?.streetPrice - product?.bestTaxable).toFixed(2) + ' €';
    } else {
      return (
        (
          ((product?.streetPrice - product?.bestTaxable) /
            product?.streetPrice) *
          100
        ).toFixed(0) + '%'
      );
    }
  }, [product?.bestTaxable, product?.streetPrice]);

  const urlTags = useMemo(() => {
    const urlTags = [];
    if (product?.brand) {
      urlTags.push(product?.brand?.replace(/ /g, '-'));
    }
    if (product?.attributes?.subcategory) {
      urlTags.push(product?.attributes?.subcategory?.replace(/ /g, '-'));
    }
    urlTags.push(product?.code);
    urlTags.push(product?.id);
    return urlTags;
  }, [
    product?.attributes?.subcategory,
    product?.brand,
    product?.code,
    product?.id,
  ]);

  const getProductName = useMemo(
    () =>
      `${product?.attributes?.brand} ${
        product?.tags?.find((tag) => tag.name === 'subcategory')?.value
          ?.translations?.[locale] || ''
      } ${
        product?.tags?.find((tag) => tag.name === 'color')?.value
          ?.translations?.[locale] || ''
      } ${
        product?.tags?.find((tag) => tag.name === 'gender')?.value
          ?.translations?.[locale] || ''
      } ${product?.code}`,
    [locale, product?.attributes?.brand, product?.code, product?.tags]
  );

  const getTitle = useMemo(
    () => `${getProductName} | BDroppy`,
    [getProductName]
  );

  const getDescription = useMemo(() => {
    if (product?.streetPrice > 0) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionSaving,
        [getProductName, amountSaving]
      ).join('');
    } else
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionNoSaving,
        [getProductName]
      ).join('');
  }, [
    TRANSLATIONS.products.detail.seo.descriptionNoSaving,
    TRANSLATIONS.products.detail.seo.descriptionSaving,
    amountSaving,
    getProductName,
    product?.streetPrice,
    translationsParameters,
  ]);

  return (
    <div>
      <Head>
        <title>{getTitle}</title>
        <link
          rel="canonical"
          href={`${process.env.DASHBOARD_URL}${router.asPath}`}
        />
        <meta property="og:title" content={getTitle} />
        <meta property="product:price:amount" content={product?.taxable} />
        <meta property="product:price:currency" content="EUR" />
        <meta
          property="og:availability"
          content={product?.availability > 0 ? 'in stock' : 'out of stock'}
        />
        <meta property="og:description" content={getDescription} />
        <meta name="description" content={getDescription} />
        <meta property="og:type" key="og:type" content="website" />
        <meta
          property="og:url"
          key="og:url"
          content={`${process.env.DASHBOARD_URL}/product/${urlTags.join(
            '-'
          )}?lang=${locale}`}
        />
        <meta
          property="og:image"
          key="og:image"
          content={`${process.env.PHOTO_ADDRESS_SMALL}/${product?.pictures?.[0]?.url}`}
        />
        <meta property="og:image:width" content="500" />
        <meta property="og:image:height" content="500" />
        <meta
          property="og:image:alt"
          content={`${product?.attributes?.brand} ${
            product?.tags?.find((tag) => tag.name === 'subcategory')?.value
              ?.translations?.[locale] || ''
          }`}
        />
        <meta property="product:brand" content={product?.attributes?.brand} />
        <meta property="product:condition" content="new" />
        <meta property="product:retailer_item_id" content={product?.code} />
      </Head>
      {isLoggedIn ? (
        <Dashboard>
          <Details product={product} />
        </Dashboard>
      ) : (
        <Main>
          <Details product={product} isPublicDetail />
        </Main>
      )}
    </div>
  );
}

export async function getServerSideProps(context) {
  const { req } = context;
  const query = context.params['id'];
  const headers = {
    origin: `https://${req.headers.host}`,
    'Content-Type': 'application/json',
  };
  try {
    const response = await request(
      `/api/product/export?ids=${query
        .split('-')
        .pop()}&acceptedlocales=en_US,it_IT,es_ES,fr_FR`,
      {
        method: 'GET',
        headers,
      }
    );
    const [item] = response.data.items;
    return { props: { product: item } };
  } catch (e) {
    return { props: { product: null } };
  }
}

export default Page;
